/**
 * useCashFlow Hook
 *
 * Loads all four cash flow views (detail, summary, trend, weekly) in parallel
 * for the active filters and exposes loading/error state.
 */

import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import CashFlowApiService from '../services/cashFlowApiService';
import {
    CashFlowDetailRow,
    CashFlowSummary,
    CashFlowTrendPoint,
    CashFlowWeeklyRow,
    CashFlowFilters,
} from '../types';
import { CASH_FLOW_DEFAULTS } from '../apiConfig';

interface UseCashFlowReturn {
    detail: CashFlowDetailRow[];
    summary: CashFlowSummary | null;
    trend: CashFlowTrendPoint[];
    weekly: CashFlowWeeklyRow[];
    loading: boolean;
    error: string | null;
    filters: CashFlowFilters;
    setFilters: (filters: CashFlowFilters) => void;
    fetchData: (filtersToUse?: CashFlowFilters) => Promise<void>;
}

export const getDefaultFilters = (): CashFlowFilters => {
    const now = new Date();
    return {
        company_id: CASH_FLOW_DEFAULTS.COMPANY_ID,
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        months: CASH_FLOW_DEFAULTS.TREND_MONTHS,
    };
};

export const useCashFlow = (): UseCashFlowReturn => {
    const toast = useToast();

    const [detail, setDetail] = useState<CashFlowDetailRow[]>([]);
    const [summary, setSummary] = useState<CashFlowSummary | null>(null);
    const [trend, setTrend] = useState<CashFlowTrendPoint[]>([]);
    const [weekly, setWeekly] = useState<CashFlowWeeklyRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<CashFlowFilters>(getDefaultFilters());

    const fetchData = useCallback(async (filtersToUse?: CashFlowFilters) => {
        const activeFilters = filtersToUse || filters;
        setLoading(true);
        setError(null);

        try {
            const [detailRes, summaryRes, trendRes, weeklyRes] = await Promise.all([
                CashFlowApiService.getDetail(activeFilters),
                CashFlowApiService.getSummary(activeFilters),
                CashFlowApiService.getTrend(activeFilters),
                CashFlowApiService.getWeekly(activeFilters),
            ]);

            setDetail(detailRes.status === 'success' ? detailRes.data || [] : []);
            setSummary(
                summaryRes.status === 'success' && summaryRes.data && summaryRes.data.length > 0
                    ? summaryRes.data[0]
                    : null,
            );
            setTrend(trendRes.status === 'success' ? trendRes.data || [] : []);
            setWeekly(weeklyRes.status === 'success' ? weeklyRes.data || [] : []);
        } catch (err: any) {
            const message = err.response?.data?.message || err.message || 'Gagal mengambil data cash flow';
            setError(message);
            setDetail([]);
            setSummary(null);
            setTrend([]);
            setWeekly([]);

            toast({
                title: 'Error',
                description: message,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right',
            });
        } finally {
            setLoading(false);
        }
    }, [filters, toast]);

    return {
        detail,
        summary,
        trend,
        weekly,
        loading,
        error,
        filters,
        setFilters,
        fetchData,
    };
};

export default useCashFlow;
